import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Chip,
  IconButton,
  DialogContentText,
  Typography,
  TextField,
  Stack,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  Menu,
  MenuItem,
} from '@material-ui/core'
import { Note, Warning, GetApp } from '@material-ui/icons'
import MUIDataTable from 'mui-datatables'
import React, { useState, useEffect } from 'react'
import DeleteIcon from '@material-ui/icons/Delete'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import { fetchGeneSet, removeGeneSet } from '../../api/gene/geneSet'
import { useBedFiles } from '../../api/file/list'
import { deleteBedFile, fetchUnmappedRegions, updateBedChemistry } from '../../api/bed'
import { useSnackbar } from 'notistack5'
import {
  CHEMISTRY_OPTIONS,
  DEFAULT_CHEMISTRY,
  chemistryLabel,
  chemistrySupported,
  ownCallingUnavailableReason,
} from 'src/utils/bedChemistry'
import ExpandOnClick from 'src/components/ExpandOnClick'
import { NewAnnotationDialog } from './AnnotationDialog'
import { fDateTime } from 'src/utils/formatTime'
import TonalChip from '../FileUploadView/components/TonalChip'
import CircularProgress from '@material-ui/core/CircularProgress'
import InfoIcon from '@material-ui/icons/Info'
import { styled, alpha } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/Search'
import CloseIcon from '@material-ui/icons/Close'
import { PATH_DASHBOARD } from 'src/routes/paths'
import { Link as RouterLink } from 'react-router-dom'
import { Icon } from '@iconify/react'
import activityFill from '@iconify/icons-eva/activity-fill'
import useAuth from '../../hooks/useAuth'

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    backgroundColor: alpha(theme.palette.background.paper, 0.9),
    backdropFilter: 'blur(10px)',
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[4],
  },
}))

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
}))

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(3),
}))

const StyledList = styled(List)(({ theme }) => ({
  maxHeight: '50vh',
  overflow: 'auto',
  '& .MuiListItem-root': {
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
  },
}))

const TruncatedChip = styled(Chip)(({ theme }) => ({
  maxWidth: '200px',
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}))

const GeneListDialog = ({ open, onClose, panel }) => {
  const [searchTerm, setSearchTerm] = useState('')

  const filteredGenes = panel?.genes?.filter((gene) => gene.toLowerCase().includes(searchTerm.toLowerCase()))

  return (
    <StyledDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <StyledDialogTitle>
        <Typography variant="h6">{panel?.value}</Typography>
        <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
          {panel?.genes?.length} genes
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <Stack spacing={2}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search genes"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <StyledList>
            {filteredGenes?.map((gene, index) => (
              <ListItem key={index}>
                <ListItemText primary={gene} />
              </ListItem>
            ))}
          </StyledList>
        </Stack>
      </StyledDialogContent>
    </StyledDialog>
  )
}

const UnmappedRegionsDialog = ({ open, onClose, regions, bedFileName }) => {
  const [searchTerm, setSearchTerm] = useState('')

  const filteredRegions = regions?.filter((region) => {
    const [chromosome, start, end, ...additionalData] = region
    const name = additionalData[0] || `${chromosome}:${start}-${end}`
    const searchString = `${chromosome}:${start}-${end} ${name}`.toLowerCase()
    return searchString.includes(searchTerm.toLowerCase())
  })

  const downloadUnmappedRegions = () => {
    if (!regions || regions.length === 0) return

    const content = regions
      .map((region) => {
        const [chromosome, start, end, ...additionalData] = region
        return `${chromosome}\t${start}\t${end}${additionalData.length > 0 ? '\t' + additionalData.join('\t') : ''}`
      })
      .join('\n')

    const blob = new Blob([content], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${bedFileName || 'unmapped_regions'}_unmapped.txt`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  return (
    <StyledDialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <StyledDialogTitle>
        <Typography variant="h6">Unmapped Regions</Typography>
        <Typography variant="caption" color="textSecondary" sx={{ fontStyle: 'italic' }}>
          {bedFileName} - {regions?.length || 0} unmapped regions
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search unmapped regions"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              startIcon={<GetApp />}
              onClick={downloadUnmappedRegions}
              disabled={!regions || regions.length === 0}
              sx={{ minWidth: 'fit-content', whiteSpace: 'nowrap' }}
            >
              Download
            </Button>
          </Box>
          <Box sx={{ width: '100%' }}>
            {filteredRegions && filteredRegions.length > 0 ? (
              <StyledList>
                {filteredRegions.map((region, index) => {
                  const [chromosome, start, end, ...additionalData] = region
                  const name = additionalData[0] || `${chromosome}:${start}-${end}`

                  return (
                    <ListItem key={index}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" fontWeight="bold">
                          {chromosome}:{start.toLocaleString()}-{end.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {name}
                        </Typography>
                        {additionalData.length > 2 && (
                          <Typography variant="caption" color="textSecondary">
                            Additional data: {additionalData.slice(2).join(', ')}
                          </Typography>
                        )}
                      </Box>
                    </ListItem>
                  )
                })}
              </StyledList>
            ) : searchTerm && regions && regions.length > 0 ? (
              <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ py: 4 }}>
                No regions match your search
              </Typography>
            ) : (
              <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ py: 4 }}>
                No unmapped regions found
              </Typography>
            )}
          </Box>
        </Stack>
      </StyledDialogContent>
    </StyledDialog>
  )
}

const DeleteFileButton = ({ onClickConfirm }) => {
  const [dialogOpen, setDialogOpen] = useState(false)

  const handleClickDelete = () => {
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
  }

  const handleClickConfirm = () => {
    setDialogOpen(false)
    onClickConfirm()
  }

  return (
    <>
      <IconButton sx={{ '&:hover': { color: 'error.main' } }} onClick={handleClickDelete}>
        <DeleteIcon />
      </IconButton>
      <Dialog open={dialogOpen} onClose={handleCloseDialog}>
        <DialogTitle>Danger: Delete file</DialogTitle>
        <Box p={0.5} />
        <DialogContent>
          <DialogContentText>Do you really want to delete this file?</DialogContentText>
          <DialogContentText>This will delete the file and all associated analyses.</DialogContentText>
          <DialogContentText>
            <strong>This action cannot be undone.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleClickConfirm} autoFocus color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

// The library chemistry of a kit: a choice for the organisation's own kits, plain
// text for the default ones. When Genesus cannot analyse that chemistry, the
// server's reason is shown under it.
export const ChemistryCell = ({ row, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [saving, setSaving] = useState(false)
  const [anchor, setAnchor] = useState(null)
  const value = row.chemistry || DEFAULT_CHEMISTRY
  const reason = ownCallingUnavailableReason(row)
  const shortLabel = CHEMISTRY_OPTIONS.find((o) => o.value === value)?.short || chemistryLabel(value)

  const choose = async (next) => {
    setAnchor(null)
    if (next === value) return
    setSaving(true)
    try {
      await updateBedChemistry(row.id, next)
      if (onSaved) await onSaved()
    } catch (err) {
      console.error('Error updating library chemistry:', err)
      enqueueSnackbar('The library chemistry could not be changed. Please try again.', { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    // Plain text with a small edit button: the kits table sits in a half-width
    // card, and a select box overflowed it.
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
          {shortLabel}
        </Typography>
        {!row.is_default && (
          <>
            <IconButton
              size="small"
              disabled={saving}
              onClick={(e) => setAnchor(e.currentTarget)}
              aria-label={`Change the library chemistry of ${row.visible_name || row.name}`}
              aria-haspopup="menu"
              sx={{ p: 0.25, color: 'primary.main' }}
            >
              <EditOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
              {CHEMISTRY_OPTIONS.map((option) => (
                <MenuItem
                  key={option.value}
                  selected={option.value === value}
                  onClick={() => choose(option.value)}
                  dense
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Box>
      {reason && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 0.5 }}
          data-testid="kit-unavailable-reason"
        >
          {reason}
        </Typography>
      )}
    </Box>
  )
}

const GeneListButton = ({ panel }) => {
  const [dialogOpen, setDialogOpen] = useState(false)

  return (
    <>
      <Chip
        label={panel.genes?.length || 0}
        onClick={() => setDialogOpen(true)}
        color="primary"
        variant="outlined"
        size="small"
        sx={{
          minWidth: '40px',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
          },
        }}
      />
      <GeneListDialog open={dialogOpen} onClose={() => setDialogOpen(false)} panel={panel} />
    </>
  )
}

const SamplesView = ({ isKits, genePanelsUpdated }) => {
  const [genePanels, setGenePanels] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isAnnotationModalOpen, setAnnotationModalOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [unmappedRegions, setUnmappedRegions] = useState({})
  const [unmappedDialogOpen, setUnmappedDialogOpen] = useState(false)
  const [selectedUnmappedData, setSelectedUnmappedData] = useState(null)

  const { query: bedFilesQuery, refresh: refreshBedFiles } = useBedFiles()

  const { user, logout } = useAuth()

  useEffect(() => {
    if (!isKits) {
      fetchGenePanels()
    } else {
      setLoading(false) // Set loading to false for kits view
    }
  }, [isKits, genePanelsUpdated])

  const fetchedBedFileIds = React.useRef(new Set())

  // Fetch unmapped regions for liftovered bed files
  useEffect(() => {
    const fetchUnmappedForLiftovered = async () => {
      if (!isKits || !bedFilesQuery.data || bedFilesQuery.isLoading) return

      const newUnmappedRegions = {}
      const fetchPromises = []

      for (const bedFile of bedFilesQuery.data) {
        if (bedFile.liftovered && !unmappedRegions[bedFile.id] && !fetchedBedFileIds.current.has(bedFile.id)) {
          // Mark as fetched immediately to prevent duplicate requests
          fetchedBedFileIds.current.add(bedFile.id)

          fetchPromises.push(
            fetchUnmappedRegions(bedFile.id)
              .then((unmapped) => {
                if (unmapped && unmapped.length > 0) {
                  newUnmappedRegions[bedFile.id] = unmapped
                }
              })
              .catch((error) => {
                console.error(`Error fetching unmapped regions for bed file ${bedFile.id}:`, error)
                // Optionally remove from fetched set on error to retry later?
                // For now, let's keep it to avoid spamming on persistent errors
              }),
          )
        }
      }

      if (fetchPromises.length > 0) {
        await Promise.all(fetchPromises)
        setUnmappedRegions((prev) => ({ ...prev, ...newUnmappedRegions }))
      }
    }

    fetchUnmappedForLiftovered()
  }, [isKits, bedFilesQuery.data, bedFilesQuery.isLoading, unmappedRegions])

  const fetchGenePanels = async () => {
    try {
      setLoading(true)
      const data = await fetchGeneSet()
      setGenePanels(data)
      setLoading(false)
    } catch (err) {
      console.error('Error fetching gene panels:', err)
      setError(err.message || 'Failed to load gene panels. Please try again later.')
      setLoading(false)
    }
  }

  const handleDeleteGenePanel = async (id) => {
    try {
      await removeGeneSet(id)
      fetchGenePanels()
    } catch (err) {
      console.error('Error deleting gene panel:', err)
      setError('Failed to delete gene panel. Please try again later.')
    }
  }

  const handleDeleteCaptureKit = async (id) => {
    try {
      await deleteBedFile(id)
      refreshBedFiles()
    } catch (err) {
      console.error('Error deleting capture kit:', err)
      setError('Failed to delete capture kit. Please try again later.')
    }
  }

  const handleUnmappedRegionsClick = (bedFile) => {
    const regions = unmappedRegions[bedFile.id]
    setSelectedUnmappedData({
      regions,
      bedFileName: bedFile.visible_name,
    })
    setUnmappedDialogOpen(true)
  }

  // Function to normalize Turkish characters to English equivalents
  const normalizeTurkishToEnglish = (str) => {
    if (!str) return str
    const turkishMap = {
      İ: 'I',
      ı: 'i',
      Ş: 'S',
      ş: 's',
      Ç: 'C',
      ç: 'c',
      Ğ: 'G',
      ğ: 'g',
      Ü: 'U',
      ü: 'u',
      Ö: 'O',
      ö: 'o',
    }
    return String(str)
      .split('')
      .map((char) => turkishMap[char] || char)
      .join('')
  }

  const KITS_COLUMNS = [
    {
      name: 'delete',
      label: 'Delete',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const row = bedFilesQuery.data[dataIndex]
          if (!row) return null

          if (row.is_default) {
            return (
              <Tooltip title="Cannot delete default file" placement="top">
                <span>
                  <IconButton disabled sx={{ color: 'action.disabled' }}>
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )
          }

          return <DeleteFileButton onClickConfirm={() => handleDeleteCaptureKit(row.id)} />
        },
      },
    },
    {
      name: 'created',
      label: 'Created At',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = bedFilesQuery.data[dataIndex]
          return row ? fDateTime(row.created) : null
        },
      },
    },
    {
      name: 'visible_name',
      label: 'Name',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = bedFilesQuery.data[dataIndex]
          if (!row) return null

          const isProcessed = row.is_processed !== false
          const isVerified = row.is_verified !== false

          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title={row.visible_name} placement="top">
                <TruncatedChip label={row.visible_name} />
              </Tooltip>
              {user.is_superuser && row.is_default === false && (
                <Tooltip title="Go to Pipeline" arrow>
                  <IconButton
                    size="small"
                    component={RouterLink}
                    to={`${PATH_DASHBOARD.pipelines}/${row.id}/tasks`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Icon icon={activityFill} width={24} height={24} />
                  </IconButton>
                </Tooltip>
              )}
              {!isProcessed ? (
                <TonalChip tone="info" label="Verifying" dot />
              ) : !isVerified ? (
                <TonalChip tone="error" label="Not verified" dot />
              ) : null}
            </Box>
          )
        },
      },
    },
    {
      name: 'reference',
      label: 'Reference',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = bedFilesQuery.data[dataIndex]
          return row ? row.reference : null
        },
      },
    },
    {
      name: 'liftovered',
      label: 'Liftover',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = bedFilesQuery.data[dataIndex]
          if (!row) return null

          return (
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
              {row.liftovered ? <TonalChip tone="success" label="Yes" /> : <TonalChip tone="error" label="No" />}
              {unmappedRegions[row.id] && (
                <Tooltip title="View unmapped regions" placement="top">
                  <Chip
                    label="U"
                    size="small"
                    onClick={() => handleUnmappedRegionsClick(row)}
                    sx={{
                      backgroundColor: 'info.main',
                      color: 'info.contrastText',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      cursor: 'pointer',
                      minWidth: '32px',
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          )
        },
      },
    },
  ]

  const CHEMISTRY_COLUMN = {
    name: 'chemistry',
    label: 'Chemistry',
    options: {
      filter: true,
      sort: true,
      customBodyRenderLite: (dataIndex) => {
        const row = bedFilesQuery.data[dataIndex]
        return row ? <ChemistryCell row={row} onSaved={refreshBedFiles} /> : null
      },
    },
  }
  // Shown once the backend reports a chemistry for its kits.
  if (chemistrySupported(bedFilesQuery.data)) KITS_COLUMNS.push(CHEMISTRY_COLUMN)

  const PANELS_COLUMNS = [
    {
      name: 'delete',
      label: 'Delete',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const row = genePanels[dataIndex]
          if (!row) return null

          return <DeleteFileButton onClickConfirm={() => handleDeleteGenePanel(row.id)} />
        },
      },
    },
    {
      name: 'created',
      label: 'Created At',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = genePanels[dataIndex]
          return row ? fDateTime(row.created) : null
        },
      },
    },
    {
      name: 'notes',
      label: 'Notes',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const row = genePanels[dataIndex]
          return row ? (
            <ExpandOnClick
              expanded={
                <Box p={2}>
                  <Typography>{row.notes}</Typography>
                  <Divider />
                  {row.modified && row.organisation && (
                    <Typography variant="caption">
                      {fDateTime(row.modified)} by {row.organisation}
                    </Typography>
                  )}
                </Box>
              }
            >
              {({ ref, onClick }) => (
                <IconButton variant="contained" ref={ref} onClick={onClick}>
                  <Note />
                </IconButton>
              )}
            </ExpandOnClick>
          ) : null
        },
      },
    },
    {
      name: 'value',
      label: 'Name',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const row = genePanels[dataIndex]
          if (!row) return null
          return (
            <Tooltip title={row.value} placement="top">
              <TruncatedChip label={row.value} />
            </Tooltip>
          )
        },
      },
    },
    {
      name: 'genes',
      label: 'Genes',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex) => {
          const row = genePanels[dataIndex]
          if (!row || !row.genes) return null

          return <GeneListButton panel={row} />
        },
      },
    },
  ]

  if ((!isKits && loading) || (isKits && bedFilesQuery.isLoading)) {
    return <CircularProgress />
  }

  if ((!isKits && error) || (isKits && bedFilesQuery.error)) {
    return <Typography color="error">{error || bedFilesQuery.error}</Typography>
  }

  const tableData = isKits ? bedFilesQuery.data || [] : genePanels
  const columns = isKits ? KITS_COLUMNS : PANELS_COLUMNS

  return (
    <>
      <NewAnnotationDialog
        open={isAnnotationModalOpen}
        onClose={() => setAnnotationModalOpen(false)}
        fileType={selectedFile?.type}
        title={selectedFile?.sample_name}
        bedFiles={genePanels}
        key={'create-analysis-dialog'}
        data={genePanels}
        currentId={selectedFile?.id}
      />
      <UnmappedRegionsDialog
        open={unmappedDialogOpen}
        onClose={() => setUnmappedDialogOpen(false)}
        regions={selectedUnmappedData?.regions}
        bedFileName={selectedUnmappedData?.bedFileName}
      />
      {isKits ? (
        <Box mb={2} marginLeft={1} sx={{ backgroundColor: 'background.paper', p: 2, borderRadius: 1 }}>
          <Typography variant="body2" display="flex" alignItems="center">
            <InfoIcon fontSize="small" color="primary" style={{ marginRight: 8 }} sx={{ fontSize: 16 }} />
            Capture Kits should be in BED file format. BED file defines genomic regions of interest and is used for
            targeted analysis. It specifies regions consisting of chromosome, start, and end positions. It may be
            offered by your sequencing provider.
          </Typography>
        </Box>
      ) : (
        <Box mb={2} marginLeft={1} sx={{ backgroundColor: 'background.paper', p: 2, borderRadius: 1 }}>
          <Typography variant="body2" display="flex" alignItems="center">
            <InfoIcon fontSize="small" color="primary" style={{ marginRight: 8 }} sx={{ fontSize: 16 }} />
            Gene Panels should be in TXT or CSV format with comma-separated values. Each line should contain a single
            gene name. Only TXT and CSV files are accepted.
          </Typography>
        </Box>
      )}
      <MUIDataTable
        title={isKits ? 'Capture Kits' : 'Gene Panels'}
        data={tableData}
        columns={columns}
        options={{
          selectableRows: 'none',
          sortOrder: { name: 'created', direction: 'desc' },
          expandableRows: false,
          print: false,
          viewColumns: true,
          download: false,
          customSearch: (searchQuery, currentRow, columns) => {
            if (!searchQuery) return true
            // Normalize Turkish characters to English and convert to lowercase
            const normalizedSearch = normalizeTurkishToEnglish(searchQuery).toLowerCase()
            return columns.some((column, index) => {
              const columnValue = currentRow[index]
              if (columnValue === null || columnValue === undefined) {
                return false
              }
              // Handle arrays (like genes array)
              if (Array.isArray(columnValue)) {
                return columnValue.some((item) => {
                  const normalizedItem = normalizeTurkishToEnglish(String(item)).toLowerCase()
                  return normalizedItem.includes(normalizedSearch)
                })
              }
              // Normalize the column value and compare
              const normalizedValue = normalizeTurkishToEnglish(String(columnValue)).toLowerCase()
              return normalizedValue.includes(normalizedSearch)
            })
          },
        }}
      />
    </>
  )
}

export default SamplesView
